// Shared free-text-reason modal for two flows with an identical shape:
// deactivate an account (POST .../deactivate) and reset MFA
// (POST .../mfa/reset). Used by both Users.tsx's row actions and
// UserDetail.tsx's Acciones card.

import { useState } from 'react'

import { Button, FormField, Input, Modal, toast } from '../../../design-system/index.js'
import { apiErrorMessage } from '../../../lib/api-error.js'
import { deactivateUser, resetMfa } from '../users-data.js'

export interface ReasonModalTarget {
  kind: 'deactivate' | 'mfa-reset'
  id: string
}

export interface ReasonModalProps {
  target: ReasonModalTarget | null
  onClose: () => void
  onSuccess: () => void | Promise<void>
}

export function ReasonModal({ target, onClose, onSuccess }: ReasonModalProps): JSX.Element {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  function close(): void {
    setReason('')
    onClose()
  }

  async function submit(): Promise<void> {
    if (!target) return
    setBusy(true)
    try {
      if (target.kind === 'deactivate') {
        await deactivateUser(target.id, reason.trim())
        toast.ok('Cuenta desactivada')
      } else {
        await resetMfa(target.id, reason.trim())
        toast.ok('MFA restablecida')
      }
      close()
      await onSuccess()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo completar la acción.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={target !== null}
      onClose={close}
      title={target?.kind === 'deactivate' ? 'Desactivar cuenta' : 'Restablecer MFA'}
      subtitle={
        target?.kind === 'deactivate'
          ? 'Su acceso queda bloqueado y sus sesiones activas se cierran.'
          : 'La persona recibirá un correo y configurará MFA de nuevo al entrar; sus sesiones se cierran.'
      }
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={busy}
            disabled={reason.trim() === ''}
          >
            {target?.kind === 'deactivate' ? 'Desactivar cuenta' : 'Restablecer MFA'}
          </Button>
        </>
      }
    >
      <FormField label="Motivo" htmlFor="reason-input">
        <Input
          id="reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explica brevemente el motivo"
        />
      </FormField>
    </Modal>
  )
}
